import { create } from 'zustand';
import { applyPatches, enablePatches, produceWithPatches, type Patch } from 'immer';
import type { FormDefinition, FormElement, FormPage, QuestionType } from './types';
import { typeMeta } from './types';
import { MAX_PER_ROW, evenWidth, rebalanceRows, rowMates, rowsOf, setWidth } from './rowLayout';

enablePatches();

interface Command {
  label: string;
  forward: Patch[];
  inverse: Patch[];
}

interface BuilderState {
  definition: FormDefinition | null;
  formId: string | null;
  selectedRef: string | null;
  /** The page the palette adds to. Follows selection so "add" lands where you are working. */
  activePageRef: string | null;
  undoStack: Command[];
  redoStack: Command[];
  dirty: boolean;
  lastSavedAt: number | null;

  addLayoutGroup: (pageRef?: string, index?: number) => void;
  load: (formId: string, definition: FormDefinition) => void;
  apply: (label: string, recipe: (draft: FormDefinition) => void) => void;
  undo: () => void;
  redo: () => void;
  select: (ref: string | null) => void;
  setActivePage: (pageRef: string) => void;
  markSaved: () => void;

  addQuestion: (type: QuestionType, pageRef?: string, index?: number) => void;
  removeElement: (ref: string) => void;
  addRepeatingGroup: (pageRef?: string) => void;
  addToGroup: (groupRef: string, type: QuestionType) => void;
  duplicateElement: (ref: string) => void;
  moveElement: (ref: string, toPageRef: string, toIndex: number) => void;
  moveIntoGroup: (ref: string, groupRef: string, toIndex?: number) => void;
  placeBeside: (ref: string, targetRef: string, side: 'left' | 'right') => void;
  addPage: () => void;
  removePage: (pageRef: string) => void;
}

/**
 * Where an element actually lives.
 *
 * Everything that moves an element used to scan `page.elements` alone, which is only half
 * the tree: a question inside a Columns block lives in that group's own `elements`, so it
 * was invisible to every one of them. Dragging one out of a group found nothing and did
 * nothing — no error, no movement, which reads as the drag being ignored.
 */
function locate(draft: FormDefinition, ref: string) {
  for (const page of draft.pages) {
    const top = page.elements.findIndex((e) => e.ref === ref);
    if (top >= 0) {
      return { page, group: undefined as FormElement | undefined, index: top };
    }

    for (const group of page.elements) {
      if (group.kind !== 'group' || !group.elements) continue;

      const inner = group.elements.findIndex((e) => e.ref === ref);
      if (inner >= 0) return { page, group, index: inner };
    }
  }

  return null;
}

/** Lifts the element out of wherever it was, leaving the tree consistent behind it. */
function detach(found: NonNullable<ReturnType<typeof locate>>): FormElement | undefined {
  const from = found.group ? found.group.elements! : found.page.elements;
  const [element] = from.splice(found.index, 1);
  return element;
}

const HISTORY_LIMIT = 100;

export const useBuilder = create<BuilderState>()((set, get) => ({
  definition: null,
  formId: null,
  selectedRef: null,
  activePageRef: null,
  undoStack: [],
  redoStack: [],
  dirty: false,
  lastSavedAt: null,

  load: (formId, definition) => {
    // Forms built before matrices seeded their columns are already saved with choices the
    // runtime ignores and no columns at all, which the publish validator refuses. Promoting
    // them here repairs the draft in place; marking it dirty is what makes that repair
    // reach the server, because otherwise Publish would send the unrepaired stored copy.
    const repaired = repairMatrices(definition);

    set({
      formId, definition,
      undoStack: [], redoStack: [],
      selectedRef: null,
      activePageRef: definition.pages[0]?.ref ?? null,
      dirty: repaired, lastSavedAt: Date.now(),
    });
  },

  /**
   * Every mutation goes through here. Immer gives us the inverse patch for free, so undo
   * does not need a hand-written opposite for each of ~40 operations — which is where
   * builders of this kind usually accumulate their subtlest bugs.
   */
  apply: (label, recipe) => {
    const current = get().definition;
    if (!current) return;

    const [next, forward, inverse] = produceWithPatches(current, recipe);
    if (forward.length === 0) return;   // no-op; don't pollute history

    set((s) => ({
      definition: next,
      undoStack: [...s.undoStack, { label, forward, inverse }].slice(-HISTORY_LIMIT),
      redoStack: [],
      dirty: true,
    }));
  },

  undo: () => {
    const { undoStack, definition } = get();
    const command = undoStack.at(-1);
    if (!command || !definition) return;

    set((s) => ({
      definition: applyPatches(definition, command.inverse),
      undoStack: s.undoStack.slice(0, -1),
      redoStack: [...s.redoStack, command],
      dirty: true,
    }));
  },

  redo: () => {
    const { redoStack, definition } = get();
    const command = redoStack.at(-1);
    if (!command || !definition) return;

    set((s) => ({
      definition: applyPatches(definition, command.forward),
      undoStack: [...s.undoStack, command],
      redoStack: s.redoStack.slice(0, -1),
      dirty: true,
    }));
  },

  select: (ref) => {
    const definition = get().definition;
    // Selecting a question also moves the insertion point onto its page, so the palette
    // adds next to what you are looking at rather than at the end of the form.
    const page = ref && definition
      ? definition.pages.find((p) => p.elements.some(
          (e) => e.ref === ref || (e.elements ?? []).some((c) => c.ref === ref)))
      : null;

    set((s) => ({ selectedRef: ref, activePageRef: page?.ref ?? s.activePageRef }));
  },

  setActivePage: (pageRef) => set({ activePageRef: pageRef }),

  markSaved: () => set({ dirty: false, lastSavedAt: Date.now() }),

  addQuestion: (type, pageRef, index) => {
    const state = get();
    const definition = state.definition;
    if (!definition) return;

    // Fall back to the active page, then the page holding the selection, then the last
    // page - never blindly to the end of the form.
    const targetRef =
      pageRef
      ?? state.activePageRef
      ?? definition.pages.at(-1)?.ref;

    if (!targetRef) return;

    // "Columns" is a container, not a question. Intercepted here so every route that adds
    // from the palette — the click, the drag onto a page, the command palette — gets the
    // same thing, rather than each having to remember the special case.
    if (type === 'columns') {
      get().addLayoutGroup(targetRef, index);
      return;
    }

    const ref = uniqueRef(definition, suggestRef(type));
    const element = newQuestion(ref, type);

    get().apply(`Add ${typeMeta(type)?.label ?? 'question'}`, (draft) => {
      const page = draft.pages.find((p) => p.ref === targetRef);
      if (!page) return;
      if (index === undefined || index >= page.elements.length) page.elements.push(element);
      else page.elements.splice(index, 0, element);
    });

    set({ activePageRef: targetRef });
    get().select(ref);
  },

  /**
   * A layout group: questions inside it sit side by side instead of stacking.
   *
   * Two columns and one starter question, because an empty container is invisible on the
   * canvas and reads as nothing having happened. The group carries no `repeat`, which is
   * what distinguishes it from the repeating group below — the runtime lays out a group
   * in columns only when it does not repeat.
   */
  addLayoutGroup: (pageRef, index) => {
    const state = get();
    const definition = state.definition;
    if (!definition) return;

    const targetRef = pageRef ?? state.activePageRef ?? definition.pages.at(-1)?.ref;
    if (!targetRef) return;

    const groupRef = uniqueRef(definition, 'columns');
    const childRef = uniqueRef(definition, 'question');

    const group: FormElement = {
      kind: 'group',
      ref: groupRef,
      title: 'Columns',
      layout: { columns: 2 },
      elements: [newQuestion(childRef, 'short_text')],
    };

    get().apply('Add columns', (draft) => {
      const page = draft.pages.find((p) => p.ref === targetRef);
      if (!page) return;
      if (index === undefined || index >= page.elements.length) page.elements.push(group);
      else page.elements.splice(index, 0, group);
    });

    set({ activePageRef: targetRef });
    get().select(groupRef);
  },

  addRepeatingGroup: (pageRef) => {
    const state = get();
    const definition = state.definition;
    if (!definition) return;

    const targetRef = pageRef ?? state.activePageRef ?? definition.pages.at(-1)?.ref;
    if (!targetRef) return;

    const groupRef = uniqueRef(definition, 'entries');
    const childRef = uniqueRef(definition, 'entry_detail');

    const group: FormElement = {
      kind: 'group',
      ref: groupRef,
      title: 'Repeating group',
      // A maximum is required by the server — it is what bounds the export width — so one
      // is chosen here rather than leaving the author with a form that will not publish.
      repeat: { min: 0, max: 5, itemLabel: 'Entry {n}', addLabel: 'Add another' },
      elements: [newQuestion(childRef, 'short_text')],
    };

    get().apply('Add repeating group', (draft) => {
      draft.pages.find((p) => p.ref === targetRef)?.elements.push(group);
    });

    set({ activePageRef: targetRef });
    get().select(groupRef);
  },

  addToGroup: (groupRef, type) => {
    const definition = get().definition;
    if (!definition) return;

    const ref = uniqueRef(definition, suggestRef(type));
    const element = newQuestion(ref, type);

    get().apply(`Add ${typeMeta(type)?.label ?? 'question'} to group`, (draft) => {
      for (const page of draft.pages) {
        const group = page.elements.find((e) => e.ref === groupRef && e.kind === 'group');
        if (group) {
          group.elements = group.elements ?? [];
          group.elements.push(element);
          return;
        }
      }
    });

    get().select(ref);
  },

  removeElement: (ref) => {
    get().apply('Delete question', (draft) => {
      for (const page of draft.pages) {
        const i = page.elements.findIndex((e) => e.ref === ref);
        if (i >= 0) {
          // Whatever shared the row closes up, rather than being left at a third of the
          // width with a hole beside it.
          const touched = new Set(rowMates(page.elements, ref));
          page.elements.splice(i, 1);
          rebalanceRows(page.elements, touched);
          return;
        }

        // Also inside groups. Without this, deleting a question in a repeating group
        // silently did nothing — the card disappeared from nowhere and came back on
        // reload, which reads as the builder losing edits.
        for (const element of page.elements) {
          const child = element.elements?.findIndex((c) => c.ref === ref) ?? -1;
          if (child >= 0) { element.elements!.splice(child, 1); return; }
        }
      }
    });

    // Logic pointing at a deleted question would fail publish validation with a
    // confusing "unknown ref"; clean it up at the source instead.
    get().apply('Clean up logic', (draft) => {
      draft.logic = draft.logic.filter((rule) => {
        const readsRef = JSON.stringify(rule.when ?? {}).includes(`"${ref}"`);
        const targetsRef = rule.actions.some((a) => a.target?.question === ref);
        return !readsRef && !targetsRef;
      });
      if (draft.scoring) delete draft.scoring.questions[ref];
    });

    if (get().selectedRef === ref) get().select(null);
  },

  duplicateElement: (ref) => {
    const definition = get().definition;
    if (!definition) return;

    let source: FormElement | undefined;
    let pageRef = '';
    let index = -1;

    for (const page of definition.pages) {
      const i = page.elements.findIndex((e) => e.ref === ref);
      if (i >= 0) { source = page.elements[i]; pageRef = page.ref; index = i; break; }
    }
    if (!source) return;

    const copy: FormElement = {
      ...structuredClone(source),
      ref: uniqueRef(definition, `${source.ref}_copy`),
    };

    get().apply('Duplicate question', (draft) => {
      const page = draft.pages.find((p) => p.ref === pageRef);
      if (!page) return;

      // The copy inherits the original's width, which would overflow a row that was already
      // full; the row takes them both in and shares itself out again.
      const touched = new Set([copy.ref, ...rowMates(page.elements, ref)]);
      page.elements.splice(index + 1, 0, copy);
      rebalanceRows(page.elements, touched);
    });

    get().select(copy.ref);
  },

  moveElement: (ref, toPageRef, toIndex) => {
    get().apply('Reorder', (draft) => {
      const target = draft.pages.find((p) => p.ref === toPageRef);
      if (!target) return;

      const found = locate(draft, ref);
      if (!found) return;

      // The rows either end of the move, noted before anything shifts. A reorder that drops
      // a full-width question into the middle of a row of three splits that row, and the
      // three widths left behind no longer add up to a full row — so both ends get closed
      // up afterwards.
      const touched = new Set<string>([ref]);

      // Only a page-level element has row mates. One coming out of a group had its width
      // decided by the group's column count, so there is no row behind it to close up.
      if (!found.group) {
        for (const mate of rowMates(found.page.elements, ref)) touched.add(mate);
      }

      const landingOn = target.elements[Math.min(toIndex, target.elements.length - 1)];
      if (landingOn) for (const mate of rowMates(target.elements, landingOn.ref)) touched.add(mate);

      const source = found.page;
      const cameFromGroup = Boolean(found.group);

      const moved = detach(found);
      if (!moved) return;

      // Inside a group the width came from the grid; on a page it has to come from the row
      // it lands in. Carrying the old value over would fight the rebalance below.
      if (cameFromGroup && moved.layout) moved.layout.width = undefined;

      target.elements.splice(Math.min(toIndex, target.elements.length), 0, moved);

      rebalanceRows(target.elements, touched);
      if (source !== target || cameFromGroup) rebalanceRows(source.elements, touched);
    });
  },

  /**
   * Moves an existing element into a layout group.
   *
   * The counterpart to dragging one out. Dropping a question onto a Columns block used to
   * fall through to the page-level move, so it landed underneath the group rather than
   * inside it — the drop appeared to work and put the question in the wrong place, which
   * is worse than refusing it.
   */
  moveIntoGroup: (ref, groupRef, toIndex) => {
    get().apply('Move into group', (draft) => {
      if (ref === groupRef) return;

      let target: FormElement | undefined;

      for (const page of draft.pages) {
        const candidate = page.elements.find((e) => e.ref === groupRef && e.kind === 'group');
        if (candidate) { target = candidate; break; }
      }

      if (!target) return;

      const found = locate(draft, ref);
      if (!found) return;

      // A container inside a container has no meaning: the runtime would be handed a grid
      // inside a grid cell with nothing deciding its width.
      const moving = found.group ? found.group.elements![found.index] : found.page.elements[found.index];
      if (!moving || moving.kind === 'group') return;

      const touched = new Set<string>([ref]);
      if (!found.group) {
        for (const mate of rowMates(found.page.elements, ref)) touched.add(mate);
      }

      const source = found.page;
      const cameFromPage = !found.group;

      const moved = detach(found);
      if (!moved) return;

      // The grid decides the width from here on.
      if (moved.layout) moved.layout.width = undefined;

      target.elements = target.elements ?? [];
      const at = toIndex ?? target.elements.length;
      target.elements.splice(Math.min(Math.max(at, 0), target.elements.length), 0, moved);

      // The row it left closes up behind it.
      if (cameFromPage) rebalanceRows(source.elements, touched);
    });
  },

  /**
   * Drops one question alongside another, and works out the widths.
   *
   * The point is that nobody has to choose a column layout first: drag a question to the
   * side of another and they share the row, two at a half each or three at a third. The
   * row it came from closes up behind it, because a question left alone at a third of the
   * width is not a layout anybody asked for — it is the debris of a move.
   *
   * That rebalancing does overwrite a width set by hand in the panel. It is what makes the
   * gesture feel automatic, and the panel is still there to narrow a lone question
   * afterwards.
   */
  placeBeside: (ref, targetRef, side) => {
    get().apply('Move beside', (draft) => {
      const from = draft.pages.find((p) => p.elements.some((e) => e.ref === ref));
      const to = draft.pages.find((p) => p.elements.some((e) => e.ref === targetRef));
      if (!from || !to || ref === targetRef) return;

      const target = to.elements.find((e) => e.ref === targetRef);
      const moving = from.elements.find((e) => e.ref === ref);

      // A group owns its row in the runtime's stylesheet, so nothing can sit beside it.
      if (!target || !moving || target.kind === 'group' || moving.kind === 'group') return;

      const targetRow = rowsOf(to.elements).find((r) => r.some((e) => e.ref === targetRef)) ?? [];
      const alreadyThere = targetRow.some((e) => e.ref === ref);

      // Three thirds fill the row; a fourth has no width the runtime can draw.
      if (!alreadyThere && targetRow.length >= MAX_PER_ROW) return;

      // Remember the row being left, so it can close up once the question is gone.
      const sourceRow = rowsOf(from.elements)
        .find((r) => r.some((e) => e.ref === ref))
        ?.filter((e) => e.ref !== ref) ?? [];

      from.elements.splice(from.elements.findIndex((e) => e.ref === ref), 1);

      const at = to.elements.findIndex((e) => e.ref === targetRef);
      to.elements.splice(side === 'left' ? at : at + 1, 0, moving);

      const width = evenWidth(alreadyThere ? targetRow.length : targetRow.length + 1);
      for (const member of targetRow) setWidth(member, width);
      setWidth(moving, width);

      // The row it left closes up behind it, because a question stranded at a third of the
      // width is not a layout anybody asked for.
      const remaining = new Set(sourceRow.filter((e) => e.ref !== ref).map((e) => e.ref));
      if (remaining.size) rebalanceRows(from.elements, remaining);
    });
  },

  addPage: () => {
    const definition = get().definition;
    if (!definition) return;

    const ref = uniqueRef(definition, `page_${definition.pages.length + 1}`);
    get().apply('Add page', (draft) => {
      draft.pages.push({ ref, title: `Page ${draft.pages.length + 1}`, elements: [] } as FormPage);
    });
    set({ activePageRef: ref });
  },

  removePage: (pageRef) => {
    if ((get().definition?.pages.length ?? 0) <= 1) return;
    get().apply('Delete page', (draft) => {
      draft.pages = draft.pages.filter((p) => p.ref !== pageRef);
    });

    if (get().activePageRef === pageRef) {
      set({ activePageRef: get().definition?.pages[0]?.ref ?? null });
    }
  },
}));

// ---- helpers ----------------------------------------------------------------------

function suggestRef(type: QuestionType): string {
  const base: Partial<Record<QuestionType, string>> = {
    email: 'email', phone: 'phone', short_text: 'question', long_text: 'comments',
    nps: 'nps_score', rating: 'rating', single_select: 'choice', multi_select: 'choices',
    dropdown: 'selection', yes_no: 'confirm', date: 'date', number: 'amount',
    searchable_dropdown: 'selection', image_choice: 'choice', star: 'rating',
    emoji_scale: 'satisfaction', password: 'answer',
  };
  return base[type] ?? type;
}

function allRefs(definition: FormDefinition): Set<string> {
  const refs = new Set<string>();
  for (const page of definition.pages) {
    refs.add(page.ref);
    for (const element of page.elements) {
      refs.add(element.ref);
      for (const child of element.elements ?? []) refs.add(child.ref);
    }
  }
  for (const variable of definition.variables) refs.add(variable.ref);
  return refs;
}

/** Refs are a public contract — they appear in exports, webhooks and prefill — so they
 *  must be unique and stable, and the builder never silently reuses one. */
export function uniqueRef(definition: FormDefinition, base: string): string {
  const taken = allRefs(definition);
  const normalised = base.toLowerCase().replace(/[^a-z0-9_]/g, '_').replace(/^[^a-z]/, 'q');
  if (!taken.has(normalised)) return normalised;

  let n = 2;
  while (taken.has(`${normalised}_${n}`)) n++;
  return `${normalised}_${n}`;
}

export function newQuestion(ref: string, type: QuestionType): FormElement {
  const meta = typeMeta(type);
  const element: FormElement = {
    kind: type === 'section_header' ? 'content' : 'question',
    ref,
    type,
    title: meta?.label ?? 'Question',
    required: false,
    elements: [],
  };

  if (meta?.hasChoices) {
    element.choices = [
      { value: 'option_1', label: 'Option 1' },
      { value: 'option_2', label: 'Option 2' },
      { value: 'option_3', label: 'Option 3' },
    ];
  }

  if (meta?.hasRows) {
    element.rows = [
      { ref: 'row_1', label: 'Row 1' },
      { ref: 'row_2', label: 'Row 2' },
    ];
  }

  // Seeded, not left empty. A matrix with no columns is refused at publish, so creating one
  // without them meant every matrix was born unpublishable with nothing on screen saying so.
  if (meta?.hasColumns) {
    element.columns = [
      { value: 'column_1', label: 'Column 1' },
      { value: 'column_2', label: 'Column 2' },
      { value: 'column_3', label: 'Column 3' },
    ];
  }

  if (type === 'nps') element.labels = { low: 'Not at all likely', high: 'Extremely likely' };
  if (type === 'rating') { element.min = 1; element.max = 5; }
  if (type === 'slider') { element.min = 0; element.max = 100; element.step = 5; }
  if (type === 'section_header') { element.contentType = 'html'; element.html = '<h2>Section</h2>'; }

  return element;
}

/**
 * Moves a matrix's choices into its columns, in place.
 *
 * A matrix is rows scored against shared columns, and the runtime only ever reads
 * `columns`. The builder used to declare matrices as choice questions, so it wrote the
 * options into `choices` — data nothing renders — and left `columns` empty, which is the
 * one thing publish rejects. The values are the same shape either way, so the old ones are
 * promoted rather than discarded: an author's column labels survive the fix.
 *
 * Returns whether anything changed, so the caller knows if the draft needs saving.
 */
function repairMatrices(definition: FormDefinition): boolean {
  let changed = false;

  for (const page of definition.pages) {
    for (const element of walk(page.elements)) {
      const meta = typeMeta(element.type ?? undefined);
      if (!meta?.hasColumns) continue;

      if (element.columns?.length) continue;

      if (element.choices?.length) {
        element.columns = element.choices;
        changed = true;
      }

      // Left over on a matrix either way: nothing reads them, and keeping them around
      // makes the next reader wonder which of the two lists is the real one.
      if (element.choices) {
        element.choices = null;
        changed = true;
      }
    }
  }

  return changed;
}

/** Every element on a page, groups flattened, so nested questions are not missed. */
function* walk(elements: FormElement[]): Generator<FormElement> {
  for (const element of elements) {
    yield element;
    if (element.elements?.length) yield* walk(element.elements);
  }
}

export function findElement(
  definition: FormDefinition | null, ref: string | null,
): { element: FormElement; pageRef: string; index: number } | null {
  if (!definition || !ref) return null;
  for (const page of definition.pages) {
    const index = page.elements.findIndex((e) => e.ref === ref);
    if (index >= 0) {
      return { element: page.elements[index]!, pageRef: page.ref, index };
    }

    // Inside a group. Without this the properties panel reports "select a question" for a
    // question that is plainly selected on the canvas.
    for (const element of page.elements) {
      const child = element.elements?.findIndex((c) => c.ref === ref) ?? -1;
      if (child >= 0) {
        return { element: element.elements![child]!, pageRef: page.ref, index: child };
      }
    }
  }
  return null;
}

/** The default document a new form starts from (mirrors FormDefinition.Empty in C#). */
export function emptyDefinition(title: string): FormDefinition {
  return {
    schemaVersion: '1.0',
    type: 'survey',
    title,
    defaultLocale: 'en',
    locales: ['en'],
    direction: 'auto',
    settings: {
      navigation: {
        progressIndicator: 'bar', allowBack: true,
        showQuestionNumbers: true, onePerPage: false, autoAdvance: false,
      },
      behaviour: {
        saveAndResume: true, resumeExpiryHours: 168, autosaveIntervalMs: 3000,
        allowMultipleSubmissions: false, allowResponseEditing: false,
        shuffleQuestions: false, confirmBeforeSubmit: false, retainHiddenAnswers: false,
      },
      limits: { onePerIdentity: 'none' },
      protection: {
        captcha: 'adaptive', honeypot: true, minCompletionSeconds: 3,
        requireSignedToken: false, requireInvite: false,
      },
      completion: {
        action: 'showOutcome', redirectDelaySeconds: 0, sendReceiptEmail: false,
      },
    },
    variables: [],
    pages: [{ ref: 'page_1', title: 'Page 1', elements: [] }],
    logic: [],
    outcomes: [{
      ref: 'outcome_default',
      when: null,
      title: 'Thanks for your time.',
      body: '<p>Your response has been recorded.</p>',
      actions: [],
    }],
  };
}
